'use client';

import { useState } from 'react';
import { BubbleActions, BubbleRows } from '@/components/chat/BubbleBlock';

import type { ChoiceSet } from '@/lib/engine/tools';

interface ChoiceChipsProps {
  choices: ChoiceSet;
  /**
   * What the user has already answered, if they have. Present = the chips have
   * been used and collapse to a receipt.
   */
  answered?: string | null;
  /**
   * False for anything but the newest reply. Older chips still RENDER (so the
   * transcript reads back correctly) but cannot be tapped: the state they were
   * asked about has moved on, and a stale "Yes, move him" three turns later
   * would act on a different job.
   */
  live?: boolean;
  onPick: (label: string) => void;
}

/**
 * Tap-to-answer chips under an assistant reply.
 *
 * The rule that shapes this: chips are a SHORTCUT, never a gate. The composer —
 * typing and the mic — stays fully available beside them, because the moment
 * buttons become the only way to answer, every question the model didn't
 * anticipate becomes a dead end. That is the oldest known failure of
 * conversational UI and it is not worth re-learning.
 */
export default function ChoiceChips({ choices, answered, live = true, onPick }: ChoiceChipsProps) {
  const { question, options } = choices;
  const [showAll, setShowAll] = useState(false);
  if (!options?.length) return null;

  // Answered: the pick became an ordinary message from the user, so there is
  // nothing left to draw. It used to leave a "You said: …" receipt, which
  // restated a line already sitting two inches below it in their own bubble.
  if (answered) return null;

  // Two short answers is a yes/no, and a yes/no is the same strip an approval
  // uses. No new component, and the commonest case gets the biggest targets.
  const plain = options.every(o => !o.detail);
  if (plain && options.length === 2) {
    return (
      <BubbleActions
        actions={options.map((option, i) => ({
          label: option.label,
          tone: i === 0 ? 'primary' : 'quiet',
          disabled: !live,
          onPress: () => onPick(option.label),
        }))}
      />
    );
  }

  /**
   * 🚨 Capped at three, with the rest behind a row of their own.
   *
   * A list you have to search is not a choice you can make, and four wrapped
   * options was the shape that made chips read as clutter. The extra row is
   * dimmed and carries a plus rather than a chevron, so it reads as *there are
   * more* and not as a fourth answer.
   *
   * The cap lives here rather than in the tool: how many fit is a question
   * about a phone screen, and every word of a tool's schema is charged on
   * every turn.
   */
  const CAP = 3;
  const capped = !showAll && options.length > CAP;
  const shown = capped ? options.slice(0, CAP) : options;

  return (
    <BubbleRows
      label={question || 'Choose an answer'}
      rows={[
        ...shown.map((option, i) => ({
          id: `${option.label}-${i}`,
          title: option.label,
          detail: option.detail,
          kind: 'answer' as const,
          onPress: () => { if (live) onPick(option.label); },
        })),
        ...(capped ? [{
          id: '__more',
          title: 'One of the others',
          kind: 'more' as const,
          onPress: () => setShowAll(true),
        }] : []),
      ]}
    />
  );
}
